<template>
	<aside class="draw-scenes-aside">
		<div class="aside-head">
			<div class="aside-head__title">
				<i class="el-icon-magic-stick"></i>
				快捷场景
				<span class="aside-head__count" v-if="scenes.length > 0">{{ scenes.length }}</span>
			</div>
			<div class="aside-head__hint">点击场景，自动填好提示词</div>
		</div>

		<!-- 列表区（不滚动，自然撑高） -->
		<div class="aside-list">
			<!-- 自定义创作（高亮 + 常驻顶部） -->
			<div
				:class="['scene-mini', 'scene-mini--custom', { 'scene-mini--active': !activeGuid }]"
				@click="onClear"
			>
				<div class="scene-mini__icon scene-mini__icon--custom">
					<i class="el-icon-edit-outline"></i>
				</div>
				<div class="scene-mini__body">
					<div class="scene-mini__name">自定义创作</div>
					<div class="scene-mini__desc">自由输入描述，从你的想法开始</div>
				</div>
				<i v-if="!activeGuid" class="el-icon-check scene-mini__check"></i>
			</div>

			<!-- 查看全部场景：紧贴自定义创作下方，渐变胶囊样式更醒目 -->
			<button
				v-if="scenes.length > DISPLAY_LIMIT"
				class="view-all-btn"
				@click="dialogVisible = true"
			>
				<i class="el-icon-menu view-all-btn__lead"></i>
				<span class="view-all-btn__text">查看全部场景</span>
				<span class="view-all-btn__badge">{{ scenes.length }}</span>
				<i class="el-icon-arrow-right view-all-btn__chev"></i>
			</button>

			<!-- 分隔线 -->
			<div class="aside-list__sep" v-if="previewScenes.length > 0">
				<span>推荐场景</span>
			</div>

			<!-- 前 N 个商户场景（精选） -->
			<div
				v-for="s in previewScenes"
				:key="s.guid"
				:class="['scene-mini', { 'scene-mini--active': activeGuid === s.guid }]"
				@click="onSelect(s)"
			>
				<div class="scene-mini__icon">
					<img v-if="s.coverImage" :src="s.coverImage" alt="" />
					<span v-else class="scene-mini__emoji">{{ s.sceneIcon || '🎨' }}</span>
				</div>
				<div class="scene-mini__body">
					<div class="scene-mini__name">{{ s.sceneName }}</div>
					<div class="scene-mini__desc">{{ s.sceneDesc || '点击应用预设提示词' }}</div>
					<div class="scene-mini__tag">{{ s.modelName }}</div>
				</div>
				<i v-if="activeGuid === s.guid" class="el-icon-check scene-mini__check"></i>
			</div>

			<!-- 空状态：商户未配置场景 -->
			<div v-if="!loading && scenes.length === 0" class="aside-list__empty">
				<div class="empty-icon">🌱</div>
				<div class="empty-text">暂无场景</div>
				<div class="empty-hint">商户尚未配置快捷场景，<br/>使用「自定义创作」自由发挥</div>
			</div>
		</div>

		<!-- ============ 全部场景弹窗（含搜索 + 大网格） ============ -->
		<el-dialog
			:visible.sync="dialogVisible"
			width="880px"
			custom-class="all-scenes-dialog"
			:append-to-body="true"
			:show-close="false"
		>
			<div slot="title" class="dialog-header">
				<div class="dialog-header__title">
					<i class="el-icon-magic-stick"></i>
					全部快捷场景
					<span class="dialog-header__count">{{ filtered.length }} / {{ scenes.length }}</span>
				</div>
				<div class="dialog-header__search">
					<i class="el-icon-search"></i>
					<input
						v-model="keyword"
						placeholder="搜索场景名称、简介或模型"
						type="text"
						autofocus
					/>
					<i v-if="keyword" class="el-icon-close clear-icon" @click="keyword = ''"></i>
				</div>
				<i class="el-icon-close dialog-header__close" @click="dialogVisible = false"></i>
			</div>

			<div class="dialog-body">
				<div v-if="filtered.length > 0" class="dialog-grid">
					<div
						v-for="s in filtered"
						:key="s.guid"
						:class="['big-scene-card', { 'big-scene-card--active': activeGuid === s.guid }]"
						@click="onDialogSelect(s)"
					>
						<div class="big-scene-card__cover">
							<img v-if="s.coverImage" :src="s.coverImage" alt="" />
							<span v-else class="big-scene-card__emoji">{{ s.sceneIcon || '🎨' }}</span>
							<span v-if="activeGuid === s.guid" class="big-scene-card__check">
								<i class="el-icon-check"></i>
							</span>
						</div>
						<div class="big-scene-card__body">
							<div class="big-scene-card__title">{{ s.sceneName }}</div>
							<div class="big-scene-card__desc">{{ s.sceneDesc || '点击应用预设提示词' }}</div>
							<div class="big-scene-card__tag">{{ s.modelName }}</div>
						</div>
					</div>
				</div>

				<div v-else class="dialog-empty">
					<i class="el-icon-search"></i>
					<span>没有匹配「{{ keyword }}」的场景</span>
				</div>
			</div>
		</el-dialog>
	</aside>
</template>

<script>
import { getDrawScenes } from '@/apis/draw';

const DISPLAY_LIMIT = 5; // 左栏精选展示数

export default {
	name: 'layout-draw-scenes',

	props: {
		_activeGuid: { type: String, default: '' },
	},

	data() {
		return {
			scenes: [],
			loading: true,
			keyword: '',          // 弹窗搜索关键词
			dialogVisible: false, // 全部场景弹窗
			DISPLAY_LIMIT,
		};
	},

	computed: {
		activeGuid() { return this._activeGuid; },

		// 弹窗内：按搜索关键词过滤
		filtered() {
			const kw = (this.keyword || '').trim().toLowerCase();
			if (!kw) return this.scenes;
			return this.scenes.filter(s =>
				(s.sceneName || '').toLowerCase().includes(kw) ||
				(s.sceneDesc || '').toLowerCase().includes(kw) ||
				(s.modelName || '').toLowerCase().includes(kw)
			);
		},

		// 左栏精选：取前 DISPLAY_LIMIT 个
		previewScenes() {
			return this.scenes.slice(0, DISPLAY_LIMIT);
		},
	},

	async mounted() {
		try {
			this.scenes = await getDrawScenes();
		} catch {
			this.scenes = [];
		} finally {
			this.loading = false;
			this.$emit('loaded', this.scenes);
		}
	},

	methods: {
		onSelect(s) {
			this.$emit('select', s);
		},
		onClear() {
			this.$emit('clear');
		},
		// 弹窗里点选场景：触发选中 + 关闭弹窗
		onDialogSelect(s) {
			this.$emit('select', s);
			this.dialogVisible = false;
			// 清空搜索关键词，下次打开重置
			this.keyword = '';
		},
	},
};
</script>

<style lang="scss" scoped>
$ink-1:    #1a1a2e;
$ink-2:    #555;
$ink-3:    #999;
$accent-1: #2c8df4;
$accent-2: #6a5cf6;
$bg-soft:  #f6f8fc;
$border-soft: #ecf0f6;

.draw-scenes-aside {
	background: #fff;
	border-radius: 16px;
	border: 1px solid $border-soft;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.aside-head {
	padding: 14px 16px 12px;
	border-bottom: 1px solid $border-soft;

	&__title {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		font-size: 14px;
		font-weight: 700;
		color: $ink-1;
		margin-bottom: 3px;

		i { color: $accent-2; font-size: 15px; }
	}

	&__count {
		font-size: 11px;
		font-weight: 600;
		color: $accent-2;
		background: #f0ecff;
		padding: 1px 7px;
		border-radius: 8px;
	}

	&__hint {
		font-size: 11px;
		color: $ink-3;
	}
}

.aside-list {
	padding: 10px 8px 12px;
	display: flex;
	flex-direction: column;
	gap: 6px;

	&__sep {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 4px 6px 2px;
		font-size: 10px;
		color: #a8b1c0;
		font-weight: 600;
		letter-spacing: 0.5px;

		span { white-space: nowrap; }

		&::before, &::after {
			content: '';
			flex: 1;
			height: 1px;
			background: $border-soft;
		}
	}

	&__empty {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 4px;
		padding: 28px 16px;
		text-align: center;

		.empty-icon { font-size: 28px; }
		.empty-text { color: $ink-2; font-size: 13px; font-weight: 500; }
		.empty-hint { color: $ink-3; font-size: 11px; line-height: 1.5; }
	}
}

// 卡片项（横向小条形）
.scene-mini {
	display: flex;
	align-items: flex-start;
	gap: 10px;
	padding: 10px;
	border-radius: 10px;
	cursor: pointer;
	transition: all 0.15s;
	border: 1px solid transparent;
	position: relative;

	&:hover {
		background: #f8f6ff;
		border-color: rgba(106, 92, 246, 0.18);
	}

	&--active {
		background: linear-gradient(135deg, #f4f0ff, #fafaff);
		border-color: $accent-2;
		box-shadow: 0 3px 10px rgba(106, 92, 246, 0.15);

		.scene-mini__name { color: $accent-2; }
	}

	// 自定义创作：始终更显眼，独立视觉风格
	&--custom {
		background: linear-gradient(135deg, #f3eeff 0%, #e7f1ff 100%);
		border: 1.5px solid rgba(106, 92, 246, 0.35);
		padding: 12px;

		.scene-mini__name {
			color: $accent-2;
			font-size: 14px;
			font-weight: 700;
		}

		.scene-mini__desc {
			color: #555;
			font-weight: 500;
		}

		&:hover {
			background: linear-gradient(135deg, #ebe2ff 0%, #dde9ff 100%);
			border-color: $accent-2;
			box-shadow: 0 6px 16px rgba(106, 92, 246, 0.18);
		}

		&.scene-mini--active {
			background: linear-gradient(135deg, #e3d6ff 0%, #d4e4ff 100%);
			border-color: $accent-2;
			box-shadow: 0 6px 18px rgba(106, 92, 246, 0.28);
		}
	}

	&__icon {
		flex-shrink: 0;
		width: 36px;
		height: 36px;
		border-radius: 8px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: linear-gradient(135deg, #ebf3ff, #ece6ff);
		overflow: hidden;

		img { width: 100%; height: 100%; object-fit: cover; }

		&--custom {
			background: linear-gradient(135deg, $accent-1, $accent-2);
			i { font-size: 18px; color: #fff; }
		}
	}

	&__emoji { font-size: 20px; line-height: 1; }

	&__body {
		flex: 1;
		min-width: 0;
		padding-right: 16px;
	}

	&__name {
		font-size: 13px;
		font-weight: 600;
		color: $ink-1;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		line-height: 1.3;
		margin-bottom: 2px;
	}

	&__desc {
		font-size: 11px;
		color: #707684;
		line-height: 1.5;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	&__tag {
		display: inline-block;
		margin-top: 5px;
		font-size: 10px;
		color: #6a7589;
		background: $bg-soft;
		padding: 1px 7px;
		border-radius: 6px;
		max-width: 100%;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__check {
		position: absolute;
		top: 10px;
		right: 10px;
		width: 18px;
		height: 18px;
		border-radius: 50%;
		background: linear-gradient(135deg, $accent-1, $accent-2);
		color: #fff;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 10px;
		box-shadow: 0 2px 6px rgba(106, 92, 246, 0.4);
	}
}

// 查看全部按钮
// 查看全部场景：紧贴「自定义创作」下方的渐变按钮，整条 picker 入口
.view-all-btn {
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	padding: 11px 14px;
	margin: 2px 0 4px;
	background: linear-gradient(135deg, #2c8df4 0%, #6a5cf6 50%, #9b6ff5 100%);
	border: none;
	border-radius: 12px;
	color: #fff;
	font-size: 13px;
	font-weight: 600;
	letter-spacing: 0.3px;
	cursor: pointer;
	box-shadow: 0 4px 14px rgba(106, 92, 246, 0.28);
	transition: all 0.2s;

	&:hover {
		transform: translateY(-1px);
		box-shadow: 0 6px 18px rgba(106, 92, 246, 0.4);
	}
	&:active {
		transform: translateY(0);
		box-shadow: 0 2px 8px rgba(106, 92, 246, 0.32);
	}

	&__lead {
		font-size: 16px;
		flex-shrink: 0;
	}

	&__text {
		flex: 1;
		text-align: left;
	}

	&__badge {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 22px;
		height: 20px;
		padding: 0 7px;
		border-radius: 10px;
		background: rgba(255, 255, 255, 0.22);
		color: #fff;
		font-size: 11px;
		font-weight: 700;
		line-height: 1;
	}

	&__chev {
		font-size: 12px;
		opacity: 0.9;
		transition: transform 0.2s;
	}

	&:hover &__chev {
		transform: translateX(2px);
	}
}
</style>

<!-- 弹窗使用全局样式（不 scoped 才能穿透 el-dialog） -->
<style lang="scss">
.all-scenes-dialog {
	border-radius: 18px !important;
	overflow: hidden !important;

	.el-dialog__header {
		padding: 0 !important;
		background: linear-gradient(135deg, #f6f8fc 0%, #f0f3f8 100%);
	}

	.el-dialog__body {
		padding: 0 !important;
	}

	.dialog-header {
		display: flex;
		align-items: center;
		gap: 16px;
		padding: 18px 22px;
		border-bottom: 1px solid #ecf0f6;

		&__title {
			display: inline-flex;
			align-items: center;
			gap: 8px;
			font-size: 16px;
			font-weight: 700;
			color: #1a1a2e;
			flex-shrink: 0;

			i { color: #6a5cf6; font-size: 18px; }
		}

		&__count {
			font-size: 12px;
			font-weight: 500;
			color: #6a5cf6;
			background: #f0ecff;
			padding: 2px 8px;
			border-radius: 10px;
		}

		&__search {
			flex: 1;
			min-width: 0;
			display: flex;
			align-items: center;
			gap: 8px;
			height: 36px;
			padding: 0 14px;
			background: #fff;
			border: 1px solid #ecf0f6;
			border-radius: 12px;
			transition: border-color 0.15s;

			&:focus-within {
				border-color: #6a5cf6;
				box-shadow: 0 0 0 3px rgba(106, 92, 246, 0.1);
			}

			i {
				font-size: 14px;
				color: #999;

				&.clear-icon {
					cursor: pointer;
					&:hover { color: #6a5cf6; }
				}
			}

			input {
				flex: 1;
				border: none;
				outline: none;
				font-size: 13px;
				color: #1a1a2e;
				background: transparent;
				min-width: 0;

				&::placeholder { color: #b0b8c5; }
			}
		}

		&__close {
			cursor: pointer;
			padding: 6px;
			border-radius: 8px;
			color: #999;
			transition: all 0.15s;

			&:hover {
				background: rgba(0, 0, 0, 0.06);
				color: #1a1a2e;
			}
		}
	}

	.dialog-body {
		padding: 20px 22px 24px;
		max-height: 540px;
		overflow-y: auto;

		&::-webkit-scrollbar { width: 5px; }
		&::-webkit-scrollbar-thumb { background: #d5dae3; border-radius: 3px; }
	}

	.dialog-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
		gap: 12px;
	}

	.dialog-empty {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 10px;
		padding: 60px 20px;
		color: #999;
		font-size: 13px;

		i { font-size: 32px; opacity: 0.5; }
	}

	// 弹窗里的大卡
	.big-scene-card {
		display: flex;
		gap: 12px;
		padding: 12px 14px;
		background: #fff;
		border-radius: 12px;
		border: 1.5px solid #ecf0f6;
		cursor: pointer;
		transition: all 0.18s;
		position: relative;

		&:hover {
			transform: translateY(-2px);
			border-color: rgba(106, 92, 246, 0.3);
			box-shadow: 0 8px 18px rgba(106, 92, 246, 0.12);
		}

		&--active {
			border-color: #6a5cf6;
			background: linear-gradient(135deg, #fafbff, #fff);
			box-shadow: 0 6px 16px rgba(106, 92, 246, 0.18);
		}

		&__cover {
			flex-shrink: 0;
			width: 48px;
			height: 48px;
			border-radius: 10px;
			overflow: hidden;
			background: linear-gradient(135deg, #ebf3ff, #ece6ff);
			display: flex;
			align-items: center;
			justify-content: center;
			position: relative;

			img { width: 100%; height: 100%; object-fit: cover; }
		}

		&__emoji { font-size: 22px; line-height: 1; }

		&__check {
			position: absolute;
			top: -4px;
			right: -4px;
			width: 18px;
			height: 18px;
			border-radius: 50%;
			background: linear-gradient(135deg, #2c8df4, #6a5cf6);
			color: #fff;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 10px;
			box-shadow: 0 2px 6px rgba(106, 92, 246, 0.45);
		}

		&__body {
			flex: 1;
			min-width: 0;
		}

		&__title {
			font-size: 13px;
			font-weight: 600;
			color: #1a1a2e;
			margin-bottom: 4px;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		&__desc {
			font-size: 11px;
			color: #707684;
			line-height: 1.55;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
			overflow: hidden;
			margin-bottom: 5px;
			min-height: 32px;
		}

		&__tag {
			display: inline-block;
			font-size: 10px;
			color: #6a7589;
			background: #f6f8fc;
			padding: 2px 7px;
			border-radius: 6px;
			max-width: 100%;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	}
}
</style>
